import { useState, useEffect, useCallback } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Eye, EyeOff,
  Shuffle, Zap, RotateCcw, Play, GraduationCap
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

const POPULAR_CATEGORIES = [
  "SCIENCE", "HISTORY", "LITERATURE", "AMERICAN HISTORY", "SPORTS",
  "WORLD GEOGRAPHY", "BUSINESS & INDUSTRY", "WORLD HISTORY", "RELIGION",
  "WORD ORIGINS", "ANIMALS", "BOOKS & AUTHORS", "ART", "GEOGRAPHY",
  "LANGUAGES", "U.S. CITIES", "MYTHOLOGY", "FOOD", "U.S. HISTORY",
  "THE BIBLE", "AUTHORS", "TELEVISION", "WORLD CAPITALS", "BIOLOGY",
  "CLASSICAL MUSIC", "OPERA", "COLLEGES & UNIVERSITIES", "ISLANDS"
];

const LEVEL_OPTIONS = [
  { value: 'college', label: 'College', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', description: 'Introductory level' },
  { value: 'adult', label: 'Adult', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', description: 'Standard difficulty' },
  { value: 'masters', label: 'Masters', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', description: 'Expert-level clues' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'mixed', label: 'Mixed', color: 'bg-gray-500/20 text-gray-300 border-gray-500/30', description: 'All values' },
  { value: 'easy', label: 'Easy', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', description: '$100-$400' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', description: '$400-$800' },
  { value: 'hard', label: 'Hard', color: 'bg-rose-500/20 text-rose-400 border-rose-500/30', description: '$800+' },
];

export default function FlashCards() {
  useSEO('Flash Cards | JepPrep', 'Rapid-fire flashcard practice with random Jeopardy-style clues from 514K+ questions.');
  
  // Setup state
  const [phase, setPhase] = useState('setup'); // setup | playing | finished
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('adult');
  const [selectedDifficulty, setSelectedDifficulty] = useState('mixed');
  const [selectedCount, setSelectedCount] = useState(20);
  
  // Game state
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCards = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedLevel) params.append('level', selectedLevel);
      if (selectedDifficulty) params.append('difficulty', selectedDifficulty);
      params.append('count', selectedCount);
      
      const res = await api.get(`/api/flashcards/random?${params.toString()}`);
      if (res.data && res.data.length > 0) {
        setCards(res.data);
        setCurrentIndex(0);
        setRevealed(false);
        setPhase('playing');
      } else {
        toast.error('No clues found for those filters. Try different options.');
      }
    } catch {
      toast.error('Failed to load flash cards');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedLevel, selectedDifficulty, selectedCount]);

  useEffect(() => {
    if (phase !== 'playing') return;
    const handleKey = (e) => {
      if (cards.length === 0) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (!revealed) {
          setRevealed(true);
        } else {
          goNext();
        }
      } else if (e.key === 'ArrowRight') {
        goNext();
      } else if (e.key === 'ArrowLeft') {
        goPrev();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const goNext = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(i => i + 1);
      setRevealed(false);
    } else {
      setPhase('finished');
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
      setRevealed(false);
    }
  };

  const resetToSetup = () => {
    setPhase('setup');
    setCards([]);
    setCurrentIndex(0);
    setRevealed(false);
  };

  const card = cards[currentIndex];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/askalex">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-8 sm:h-12 shrink-0" />
              <h1 className="text-base sm:text-xl font-display text-white flex items-center gap-2">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--brand-gold-500))]" />
                Flash Cards
              </h1>
            </div>
            {phase === 'playing' && cards.length > 0 && (
              <span className="text-gray-400 text-sm sm:text-base shrink-0" data-testid="flashcard-counter">
                {currentIndex + 1} / {cards.length}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Progress bar */}
      {phase === 'playing' && cards.length > 0 && (
        <div className="bg-[hsl(var(--secondary))]">
          <div
            className="h-1 bg-[hsl(var(--brand-gold-500))] transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / cards.length) * 100}%` }}
          />
        </div>
      )}

      <main className="max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        
        {/* SETUP PHASE */}
        {phase === 'setup' && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]" data-testid="flashcard-setup">
            <CardContent className="p-5 sm:p-8">
              <div className="text-center mb-6 sm:mb-8">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Flash Cards</h2>
                <p className="text-gray-400 max-w-md mx-auto text-sm sm:text-base">
                  Flip through clues to test your recall. Configure your deck below.
                </p>
              </div>

              {/* Category Selection */}
              <div className="mb-6">
                <label className="text-white font-medium mb-3 block">Category</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm border transition-all ${
                      selectedCategory === null
                        ? 'border-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))]/10 text-[hsl(var(--brand-gold-500))]'
                        : 'border-[hsl(var(--border))] text-gray-400 hover:border-[hsl(var(--brand-gold-500))]/50 hover:text-white'
                    }`}
                    data-testid="flashcard-category-random"
                  >
                    Random
                  </button>
                  {POPULAR_CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm border transition-all ${
                        selectedCategory === cat
                          ? 'border-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))]/10 text-[hsl(var(--brand-gold-500))]'
                          : 'border-[hsl(var(--border))] text-gray-400 hover:border-[hsl(var(--brand-gold-500))]/50 hover:text-white'
                      }`}
                      data-testid={`flashcard-category-${cat.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Level Selection */}
              <div className="mb-6">
                <label className="text-white font-medium mb-3 block flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-gray-400" />
                  Level
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {LEVEL_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedLevel(opt.value)}
                      className={`p-3 sm:p-4 rounded-xl border transition-all text-center ${
                        selectedLevel === opt.value
                          ? 'border-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))]/10'
                          : 'border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 hover:bg-[hsl(var(--brand-blue-800))]'
                      }`}
                      data-testid={`flashcard-level-${opt.value}`}
                    >
                      <Badge className={`${opt.color} mb-1.5 text-xs`}>{opt.label}</Badge>
                      <p className="text-gray-400 text-xs">{opt.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty Selection */}
              <div className="mb-6">
                <label className="text-white font-medium mb-3 block">Difficulty</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {DIFFICULTY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedDifficulty(opt.value)}
                      className={`p-3 sm:p-4 rounded-xl border transition-all text-center ${
                        selectedDifficulty === opt.value
                          ? 'border-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))]/10'
                          : 'border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 hover:bg-[hsl(var(--brand-blue-800))]'
                      }`}
                      data-testid={`flashcard-difficulty-${opt.value}`}
                    >
                      <Badge className={`${opt.color} mb-1.5 text-xs`}>{opt.label}</Badge>
                      <p className="text-gray-400 text-xs">{opt.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Number of Cards */}
              <div className="mb-8">
                <label className="text-white font-medium mb-3 block">Number of Cards</label>
                <div className="flex gap-3">
                  {[10, 20].map((n) => (
                    <button
                      key={n}
                      onClick={() => setSelectedCount(n)}
                      className={`flex-1 py-3 rounded-xl border transition-all font-medium ${
                        selectedCount === n
                          ? 'border-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]'
                          : 'border-[hsl(var(--border))] text-white hover:border-[hsl(var(--brand-gold-500))]/50 hover:bg-[hsl(var(--brand-blue-800))]'
                      }`}
                      data-testid={`flashcard-count-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={loadCards}
                disabled={loading}
                className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg font-semibold"
                data-testid="flashcard-start-btn"
              >
                {loading ? 'Shuffling...' : 'Start Flash Cards'}
                <Play className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* PLAYING PHASE */}
        {phase === 'playing' && card && (
          <div className="max-w-2xl mx-auto">
            {/* The Card */}
            <Card
              className="bg-[hsl(var(--brand-blue-800))] border-2 border-[hsl(var(--border))] cursor-pointer select-none transition-all hover:border-[hsl(var(--brand-gold-500))]/50 min-h-[320px] sm:min-h-[360px] flex flex-col"
              onClick={() => setRevealed(r => !r)}
              data-testid="flashcard-card"
            >
              <div className="p-5 sm:p-8 flex flex-col flex-1">
                {/* Category & Value header */}
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <span className="text-[hsl(var(--brand-gold-500))] text-xs sm:text-sm font-semibold uppercase tracking-wide truncate max-w-[70%]">
                    {card.category}
                  </span>
                  {card.value && (
                    <span className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] text-xs sm:text-sm font-bold px-2.5 py-0.5 rounded shrink-0">
                      ${card.value}
                    </span>
                  )}
                </div>

                {/* Question */}
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-white text-lg sm:text-2xl text-center leading-relaxed" data-testid="flashcard-question">
                    {card.question}
                  </p>
                </div>

                {/* Reveal / Answer area */}
                <div className="mt-4 sm:mt-6 pt-4 border-t border-[hsl(var(--border))]">
                  {revealed ? (
                    <div className="text-center" data-testid="flashcard-answer">
                      <p className="text-[hsl(var(--brand-gold-500))] font-bold text-base sm:text-xl">
                        {card.answer}
                      </p>
                      <p className="text-gray-500 text-xs mt-2 flex items-center justify-center gap-1">
                        <EyeOff className="w-3 h-3" /> Tap to hide
                      </p>
                    </div>
                  ) : (
                    <button
                      className="w-full flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-colors py-2"
                      data-testid="flashcard-reveal-btn"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="text-sm">Tap to reveal answer</span>
                    </button>
                  )}
                </div>
              </div>
            </Card>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <Button
                variant="ghost"
                onClick={goPrev}
                disabled={currentIndex === 0}
                className="text-gray-400 hover:text-white disabled:opacity-30"
                data-testid="flashcard-prev"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Prev
              </Button>

              <button
                onClick={() => { setRevealed(false); goNext(); }}
                className="text-gray-500 hover:text-gray-300 text-xs flex items-center gap-1 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Skip
              </button>

              <Button
                variant="ghost"
                onClick={() => { setRevealed(false); goNext(); }}
                className="text-gray-400 hover:text-white"
                data-testid="flashcard-next"
              >
                {currentIndex === cards.length - 1 ? 'Finish' : 'Next'}
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>

            {/* Keyboard hint */}
            <p className="text-center text-gray-600 text-xs mt-6 hidden sm:block">
              Space/Enter to reveal &middot; Arrow keys to navigate
            </p>
          </div>
        )}

        {/* FINISHED PHASE */}
        {phase === 'finished' && (
          <div className="text-center py-12 max-w-2xl mx-auto" data-testid="flashcard-done">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center">
              <Shuffle className="w-10 h-10 text-[hsl(var(--brand-gold-500))]" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-3">
              Deck Complete!
            </h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              You've gone through all {cards.length} cards. Ready for another round?
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={loadCards}
                className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white font-semibold px-8 py-5"
                data-testid="flashcard-shuffle-again"
              >
                <Shuffle className="w-4 h-4 mr-2" />
                Shuffle Again
              </Button>
              <Button
                onClick={resetToSetup}
                variant="outline"
                className="border-[hsl(var(--border))] text-white py-5"
                data-testid="flashcard-new-setup"
              >
                New Configuration
              </Button>
              <Link to="/askalex">
                <Button variant="outline" className="border-[hsl(var(--border))] text-white w-full py-5">
                  Back to AskAlex
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
